// WHAT: Tab component for displaying Yazio nutrition goals.
// WHY:  Provides interface for viewing daily nutrition goals (calories, macros, etc.) from Yazio integration with auto-loading and error handling.
// DOMAIN: Health → Yazio
// DEPENDENCIES: React, ../../hooks/useYazioGoals, @/components/ui/Button, @tracq/frontend-shared/utils
// RISKS:
//   - WARN: Auto-loading on mount depends on useYazioGoals hook; ensure credentials are configured.
//   - NOTE: Error handling redirects to settings; ensure settings route exists.
//   - NOTE: Goals data structure is assumed; may fail with unexpected API responses.

import { useEffect } from "react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/shared/utils";

import { useYazioGoals } from "../../hooks/use-yazio-goals";

/**
 * WHAT:
 *   Tab component for displaying Yazio nutrition goals.
 *
 * WHY:
 *   Provides users with a view of their daily nutrition goals (calories, macros, etc.)
 *   from the Yazio integration, with automatic data loading and comprehensive error handling.
 *
 * BEHAVIOR:
 *   - Automatically loads today's goals data on mount if not already loaded
 *   - Displays loading state with spinner during data fetch
 *   - Shows error state with retry and dismiss options
 *   - Renders empty state with configuration link when no data is available
 *   - Displays goals cards with progress indicators for different nutrition metrics
 *   - Provides refresh functionality to reload goals data
 *
 * RISKS:
 *   - WARN: Auto-loading on mount depends on useYazioGoals hook; ensure credentials are configured.
 *   - NOTE: Error handling redirects to settings; ensure settings route exists.
 *   - NOTE: Goals data structure is assumed; may fail with unexpected API responses.
 *   - PERF: Goals array mapping may impact render performance with many goals.
 *
 * @returns {JSX.Element} Yazio goals tab component
 *
 */
export function YazioGoalsTab() {
  const { clearError, data, error, isLoading, loadTodayData, refreshData } = useYazioGoals();

  /**
   * WHAT:
   *   Effect hook to automatically load today's goals data on component mount.
   *
   * WHY:
   *   Ensures goals are displayed immediately when the tab is opened without requiring manual refresh.
   *
   * SIDE EFFECTS:
   *   - Calls loadTodayData if data is not already loaded and no error exists
   *
   * RISKS:
   *   - NOTE: Dependency on loadTodayData; ensure it's stable or memoized to prevent infinite loops.
   *   - WARN: Only loads if no data, not loading, and no error; may not retry after errors.
   */
  useEffect(() => {
    if (!data && !isLoading && !error) {
      loadTodayData();
    }
  }, [data, isLoading, error, loadTodayData]);

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <svg
              className="w-6 h-6 text-red-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-red-400">Error Loading Goals</h3>
              <p className="text-red-300 mt-1">{error}</p>
            </div>
          </div>
          <div className="flex space-x-3 mt-4">
            <Button onClick={refreshData} size="sm" variant="outline">
              Try Again
            </Button>
            <Button onClick={clearError} size="sm" variant="outline">
              Dismiss
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-500 border-t-transparent mx-auto mb-4" />
            <p className="text-slate-400">Loading your goals...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 text-center">
          <div className="w-16 h-16 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-orange-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">No Goals Data</h3>
          <p className="text-slate-400 mb-6">
            No nutrition goals found for today. Make sure your Yazio credentials are configured.
          </p>
          <div className="flex space-x-3 justify-center">
            <Button onClick={loadTodayData} variant="primary">
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
              Try Again
            </Button>
            <Button onClick={() => (globalThis.location.href = "/settings")} variant="outline">
              Configure Credentials
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const goals = [
    {
      bgColor: "bg-orange-500/20",
      color: "text-orange-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Energy (Calories)",
      unit: "kcal",
      value: data.energyGoal,
    },
    {
      bgColor: "bg-red-500/20",
      color: "text-red-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Protein",
      unit: "g",
      value: data.proteinGoal,
    },
    {
      bgColor: "bg-yellow-500/20",
      color: "text-yellow-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Fat",
      unit: "g",
      value: data.fatGoal,
    },
    {
      bgColor: "bg-green-500/20",
      color: "text-green-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Carbohydrates",
      unit: "g",
      value: data.carbGoal,
    },
    {
      bgColor: "bg-blue-500/20",
      color: "text-blue-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Water",
      unit: "L",
      value: data.waterGoal,
    },
    {
      bgColor: "bg-purple-500/20",
      color: "text-purple-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M13 10V3L4 14h7v7l9-11h-7z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Steps",
      unit: "steps",
      value: data.stepsGoal,
    },
    {
      bgColor: "bg-indigo-500/20",
      color: "text-indigo-400",
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      ),
      title: "Weight Goal",
      unit: "kg",
      value: data.weightGoal,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header with refresh button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Nutrition Goals</h2>
          <p className="text-slate-400 mt-1">
            Your daily nutrition targets for {new Date(data.date).toLocaleDateString("de-DE")}
          </p>
        </div>
        <Button disabled={isLoading} onClick={refreshData} size="sm" variant="outline">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
            />
          </svg>
          Refresh
        </Button>
      </div>

      {/* Goals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {goals.map((goal) => (
          <div
            className={cn(
              "bg-slate-800/50 rounded-lg p-6 border border-slate-700 hover:border-slate-600 transition-colors",
            )}
            key={goal.title}
          >
            <div className="flex items-center space-x-4">
              <div
                className={cn(
                  "w-12 h-12 rounded-lg flex items-center justify-center",
                  goal.bgColor,
                )}
              >
                <div className={goal.color}>{goal.icon}</div>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-white">{goal.title}</h3>
                <div className="text-2xl font-bold text-white mt-1">
                  {goal.value.toLocaleString("de-DE", {
                    maximumFractionDigits: goal.unit === "kcal" || goal.unit === "steps" ? 0 : 1,
                  })}
                  <span className="text-sm font-normal text-slate-400 ml-1">{goal.unit}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Info Card */}
      <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700">
        <div className="flex items-start space-x-3">
          <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
            <svg
              className="w-4 h-4 text-blue-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
              />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white mb-2">About Your Goals</h3>
            <p className="text-slate-400">
              These are your daily nutrition targets set in the Yazio app. Goals are personalized
              based on your profile, activity level, and health objectives. You can modify these
              goals directly in the Yazio app.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
